import { AiHarnessSelection, AiHarnessVersion } from './ai-harness-version.js';
import { AiHarnessProfile } from './ai-harness-profile.js';
import { z } from 'zod';
import { AiReasoning, AiRunLimits, AiRunLimitKey, ConstraintSource, RunLimitKind, ToolDenyReason, AiStopReason, ResolvedCritique, } from './ai-policy.js';
import { ThinkingStyle } from './ai-model-capability.js';
import { ClarifyQuestion } from './plan-draft.js';
import { AiRunStage, stageOfError } from './ai-errors.js';
import { ContextBlock, ContextDropped } from './ai-context.js';
/**
 * Dấu vết một lần chạy skill AI — APM-1302.
 *
 * ## Vì sao dấu vết này là một contract, không phải log
 *
 * Yêu cầu: người bấm nút AI phải **thấy** trợ lý đang làm gì — nó nghĩ gì, gọi
 * công cụ nào, nhận lại được gì. Không phải để trang trí: mọi kết quả AI ở hệ
 * thống này đều là **gợi ý chờ người duyệt** (luật #6), và người duyệt không
 * thể duyệt một con số rơi từ trên trời xuống. Thấy được "nó đã đọc 42 task
 * của sprint hiện tại rồi mới nói con số đó" là điều kiện để câu trả lời có
 * thể bị bác bỏ một cách có cơ sở.
 *
 * ## Vì sao bước KHÔNG lưu xuống DB
 *
 * Dấu vết sống đúng bằng thời gian một lần chạy — vài giây tới một phút — và
 * thứ đáng lưu của lần chạy đó đã có chỗ ngồi rồi: `ai_usage_log` giữ token và
 * chi phí, `ai_suggestion` giữ kết quả và ai duyệt. Thêm một bảng nữa cho phần
 * kể chuyện là thêm một bảng phình theo lượt dùng mà không ai đọc lại sau ngày
 * hôm đó. Nên tracker giữ trong RAM, và nó **mất khi restart** — chấp nhận
 * được, vì mất một dấu vết không mất kết quả nào.
 */
/** Bước "nghĩ": phần văn bản model viết trước khi gọi tool hoặc trước khi kết luận. */
export const AiRunThinking = z.object({
    kind: z.literal('thinking'),
    text: z.string(),
});
/** Bước "gọi công cụ": tên tool và tham số đã được rút gọn. */
export const AiRunToolCall = z.object({
    kind: z.literal('tool_call'),
    tool: z.string(),
    /** Tham số dạng người đọc được, đã cắt ngắn. */
    input: z.string(),
});
export const AiRunToolResult = z.object({
    kind: z.literal('tool_result'),
    tool: z.string(),
    ok: z.boolean(),
    /** Kết quả đã rút gọn — vài trăm ký tự đầu, hoặc "42 mục". */
    summary: z.string(),
    ms: z.number().int().nonnegative(),
    /** Vì sao không chạy, khi `ok = false` — APM-H-c. Thiếu = lỗi cũ chưa phân loại. */
    error: ToolDenyReason.optional(),
    /** `true` khi kết quả lấy từ run gốc (chế độ `replay`), không chạy tool thật — APM-H-e. */
    replayed: z.boolean().optional(),
    /** `true` khi trả lại kết quả đã có trong run (cùng tool, cùng tham số) — APM-H-o. */
    memo: z.boolean().optional(),
    /** Số tên/email đã ẩn danh trong kết quả này khi `redaction` bật — APM-H-o. */
    redacted: z.number().int().nonnegative().optional(),
    /** Lỗi hạ tầng (`error = terminal`): tham chiếu dòng log `runId:ref` — APM-H-m. */
    logRef: z.string().optional(),
    /**
     * Biên nhận `r1`, `r2`… của kết quả thành công — APM-H-n. Cũng là thuộc tính
     * `receipt` của thẻ `<du_lieu_he_thong>` gửi model; output trích `receipt` thì
     * grounding kiểm theo nó.
     */
    receipt: z.string().optional(),
    /**
     * Ba trường **ẩn theo trường** (APM-H-e) — chỉ ghi DB, bị lược khỏi RAM/SSE và
     * khỏi bản trả cho người không có quyền quản trị AI. Xem `HIDDEN_RUN_STEP_FIELDS`.
     */
    /** Tham số đã kiểm Zod, nguyên vẹn — khoá để phát lại theo `(tool, input)`. */
    input: z.unknown().optional(),
    /** Kết quả handler trả về, nguyên vẹn — nguồn của chế độ `replay`. */
    output: z.unknown().optional(),
    /** Chuỗi model **thật sự** nhận (đã bọc, đã cắt) — bằng chứng "model đã thấy gì". */
    sentToModel: z.string().optional(),
    /**
     * Bằng chứng nguyên văn so với thứ model nhận — APM-H-ap.
     *
     * `output` là bản **trước** `ContextBudget` (kết quả handler trả về), `sentToModel`
     * là bản sau. `truncatedForModel` nói hai bản khác nhau vì ngân sách ngữ cảnh;
     * `chars`/`charsForModel` là độ dài JSON của từng bản. `truncatedForTrace` chỉ có
     * khi chính `output` chạm trần lưu trữ (`ai.policy.trace_max_result_chars`) — khi ấy
     * `output` là **chuỗi** JSON bị cắt, không phải object, và `chars` vẫn là độ dài gốc.
     */
    truncatedForModel: z.boolean().optional(),
    chars: z.number().int().nonnegative().optional(),
    charsForModel: z.number().int().nonnegative().optional(),
    truncatedForTrace: z.boolean().optional(),
    /**
     * `tool_use.id` của lời gọi đã sinh ra kết quả này — APM-UX-ai3.
     *
     * Cần để **dựng lại hội thoại** khi chạy tiếp một run dở: mỗi `tool_use` của
     * lượt assistant phải có đúng một `tool_result` mang cùng id, và API từ chối
     * lượt sau nếu thiếu. Ghép theo thứ tự thì không dùng được — runner dispatch
     * các tool trong một lượt bằng `Promise.all`, nên thứ tự bước `tool_result`
     * là thứ tự **chạy xong**, không phải thứ tự model gọi.
     */
    useId: z.string().optional(),
});
/**
 * Bước đầu tiên của mọi run — APM-H-c: run này chạy dưới luật nào.
 *
 * `limits` là bộ trần đã gộp, `sources` nói từng số tới từ đâu, `clamped` là
 * chỗ skill xin quá trần. `hiddenTools` là tool bị ẩn vì người bấm thiếu quyền
 * — trước đây chỉ có `logger.debug`, và người debug không trả lời được "vì sao
 * model không dùng tool X".
 */
export const AiRunPolicy = z.object({
    kind: z.literal('policy'),
    harness: AiHarnessSelection.optional(),
    limits: AiRunLimits,
    sources: z.record(AiRunLimitKey, ConstraintSource),
    clamped: z.array(z.object({ key: AiRunLimitKey, requested: z.number(), applied: z.number() })),
    hiddenTools: z.array(z.object({ tool: z.string(), permission: z.string() })),
    askUserEnabled: z.boolean(),
    /** Chuỗi hook đã lắp, theo thứ tự — "assembly descriptor" của run (APM-H-q). */
    hooks: z.array(z.string()).optional(),
    /** Hook bị gỡ vì ablation (`ai.policy.disabled_hooks`, chỉ sandbox) — APM-H-ad. */
    disabledHooks: z.array(z.string()).optional(),
    /** Bản prompt ghi đè đã dùng (`ai_prompt_override.version`); `null` = prompt trong code — APM-H-f. */
    promptVersion: z.string().nullable().optional(),
    /** Mục `org_knowledge` đã chèn vào system prompt — APM-H-i. */
    knowledge: z.array(z.string()).optional(),
    /** Mức suy nghĩ đã gộp và cách model diễn đạt nó — APM-H-l. */
    reasoning: z
        .object({
        requested: AiReasoning,
        applied: AiReasoning,
        source: z.enum(['default', 'org', 'skill', 'clamped']),
        style: ThinkingStyle,
        /** `scratchpad` khi model không suy nghĩ được mà skill cần. */
        mode: z.enum(['thinking', 'scratchpad', 'none']),
    })
        .optional(),
    /** Băm của system prompt đã gửi: hai run cùng skill cùng dự án phải có cùng hash để cache trúng. */
    systemHash: z.string().optional(),
    /** Tần suất đã dùng của người bấm lúc bắt đầu run, so với trần — APM-H-o. */
    rateLimit: z
        .object({
        perUserPerMinute: z.number().int(),
        usedPerMinute: z.number().int(),
        perSkillPerUserPerHour: z.number().int(),
        usedPerHour: z.number().int(),
    })
        .optional(),
    /** Run này có ẩn danh tên/email không — APM-H-o. */
    redaction: z.boolean().optional(),
    /** Agent phản biện đã gộp cho run này — APM-CRIT-b. Thiếu = dấu vết trước bản này. */
    critique: ResolvedCritique.optional(),
    /** Trần lưu trữ của dấu vết cho run này — APM-H-ap. Thiếu = dấu vết trước bản này. */
    trace: z.object({ maxResultChars: z.number().int().positive() }).optional(),
});
/** Chạm một trần — APM-H-c. `context` là cắt kết quả tool (run vẫn tiếp), còn lại kết thúc run. */
export const AiRunLimit = z.object({
    kind: z.literal('limit'),
    limit: RunLimitKind,
    tool: z.string().optional(),
    value: z.number().optional(),
    max: z.number().optional(),
    kept: z.number().int().optional(),
    total: z.number().int().optional(),
});
/** Model hỏi lại (`ask_user`) — APM-806. Không phải lỗi, là một kết cục hợp lệ. */
export const AiRunQuestion = z.object({
    kind: z.literal('question'),
    questions: z.array(ClarifyQuestion),
});
/** Người dùng đã trả lời — ghi lại để dấu vết đọc như một cuộc đối thoại. */
export const AiRunAnswer = z.object({
    kind: z.literal('answer'),
    answers: z.record(z.string(), z.string()),
});
/**
 * Lượt chạy này **nối tiếp** một lượt đã dở — APM-UX-ai3.
 *
 * Ghi thành một bước chứ không im lặng: dấu vết sau đó nhảy từ lượt 1 sang lượt
 * 4 mà không có gì giải thích, và người đọc sẽ tưởng mất bước. Nó cũng là chỗ
 * duy nhất nói ra rằng phần đầu hội thoại là **dữ liệu cũ** — tool của những
 * lượt ấy không chạy lại, nên con số trong đó là con số của hôm qua.
 */
export const AiRunResumed = z.object({
    kind: z.literal('resume'),
    /** Lượt cuối cùng còn trọn vẹn của run trước; lượt này chạy từ `fromTurn + 1`. */
    fromTurn: z.number().int().nonnegative(),
    toolsUsed: z.array(z.string()),
});
/**
 * Thời gian một run tiêu vào đâu — APM-UX-ai5.
 *
 * ## Vì sao là một bước, không phải vài trường trên `done`
 *
 * Run **hỏng** cũng tốn thời gian, và đó thường là những run tốn nhiều nhất
 * (chạm trần lượt, verify retry ba lần). `done` chỉ có ở run xong; gắn số liệu
 * vào đó là mất đúng nửa dữ liệu đáng tối ưu.
 *
 * ## Vì sao KHÔNG ẩn như `usage`/`prompt`
 *
 * Nó chỉ chứa **số giây**. Không tên task, không nội dung prompt, không một
 * trường chuỗi nào ngoài tên pha — mà tên pha là tên hook và tên tool, những
 * thứ đã hiện sẵn ở bước `policy` và `tool_call`. Ẩn nó đi chỉ khiến người bấm
 * nút không biết vì sao mình chờ lâu.
 */
export const AiRunTiming = z.object({
    kind: z.literal('timing'),
    /** Tổng thời gian trong `AgentRunner`, từ lúc vào `run()` tới lúc ra. */
    totalMs: z.number().int().nonnegative(),
    /**
     * ms theo pha, đã cộng dồn mọi lần chạy của pha ấy.
     *
     * Khoá: `prepare`, `hook:<Tên>` (chuỗi `beforeRun`), `model` (cả lượt, gồm
     * `beforeModel`/`afterModel`), `tool:<tên tool>`, `verify` (gồm `finalize`),
     * `after:<Tên>`. Tổng các pha **nhỏ hơn** `totalMs`: phần chênh là việc của
     * chính vòng lặp (dựng message, ghép lịch sử).
     */
    phases: z.record(z.string(), z.number().int().nonnegative()),
});
export const AiRunDone = z.object({
    kind: z.literal('done'),
    /** Một dòng nói kết quả là gì. Do skill hoặc người gọi đặt. */
    summary: z.string(),
    costUsd: z.string(),
    turns: z.number().int().positive(),
    /**
     * Kết quả một phần vì chạm trần — APM-H-p. Khác `verify.partial` (thiếu vì
     * `finalize` loại): đây là thiếu vì **chưa đọc đủ**. `stopReason` có mặt là biết loại nào.
     */
    partial: z.boolean().optional(),
    stopReason: AiStopReason.optional(),
});
export const AiRunFailed = z.object({
    kind: z.literal('error'),
    message: z.string(),
    /** Mã lỗi có cấu trúc (`AI_OUTPUT_INVALID`…) — APM-H-a. Thiếu = lỗi không phân loại. */
    code: z.string().optional(),
    /** Nối tới dòng log của người vận hành (`runId:ref`) — APM-H-m. Message gốc của hạ tầng không ở đây. */
    logRef: z.string().optional(),
});
/**
 * Một lời gọi model — APM-H-a. **Chỉ ghi DB**, không hiện ở bảng bên phải.
 *
 * Bốn cột token riêng (luật #4). `cacheReadInputTokens` là con số duy nhất nói
 * cache prompt có trúng hay không — bằng 0 ở lượt hai trở đi nghĩa là system
 * prompt đang đổi giữa các lượt, hoặc gateway bỏ qua `cache_control`.
 */
export const AiRunUsage = z.object({
    harnessVersion: AiHarnessVersion.optional(),
    harness: z
        .object({
        profile: AiHarnessProfile,
        source: z.enum(['model', 'default', 'tier']),
        maxOutputTokens: z.number().int().positive().optional(),
    })
        .optional(),
    kind: z.literal('usage'),
    turn: z.number().int().positive(),
    /**
     * Trần output token mà **lượt chạy** xin cho mỗi lời gọi model (`AiSkillRequest.maxOutputTokens`)
     * — APM-BIL-a, plan-chilldoc-bilingual §3.2. Đặt cạnh `outputTokens` để `Đánh giá harness` đo được
     * độ lệch giữa trần ước lượng và token thật. Thiếu = lượt chạy không xin trần riêng.
     */
    outputCap: z.number().int().positive().optional(),
    modelId: z.string(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheReadInputTokens: z.number().int().nonnegative(),
    cacheCreationInputTokens: z.number().int().nonnegative(),
    costUsd: z.string(),
    latencyMs: z.number().int().nonnegative(),
    /**
     * Ước lượng token vào của **chính** lời gọi này, theo `estimateTokens` — APM-H-ah.
     *
     * Có mặt để đối chiếu với `inputTokens + cacheReadInputTokens` ngay bên cạnh.
     * Đây là cái thước mà **mọi** trần ngữ cảnh đang dùng (lớp 3: 1 200, bộ nhớ:
     * 1 500, lý do từ chối: 500, kết quả tool: 4 000, `max_input_tokens`: 150 000)
     * — chưa ai trừ hai con số ấy, nên chưa ai biết các trần đó chặt hay lỏng
     * bao nhiêu so với thực tế.
     *
     * Đo **cùng cách** ngân sách đo: `estimateTokens` trên chuỗi system cộng JSON
     * của messages và tools. Không phải cách tokenizer thật nhìn (nó thấy chat
     * template, không thấy JSON) — nhưng đó chính là điểm: thứ cần hiệu chỉnh là
     * con số mà các trần được viết bằng, không phải một con số lý thuyết.
     */
    estimatedInputTokens: z.number().int().nonnegative().optional(),
    /** Dòng `ai_usage_log` tương ứng, để lần ngược từ run sang báo cáo chi phí. */
    usageLogId: z.string().optional(),
    /** `end_turn | tool_use | max_tokens | refusal…` — APM-H-m. `max_tokens` là lượt bị cắt giữa chừng. */
    stopReason: z.string().nullable().optional(),
    /**
     * Suy nghĩ của lượt — APM-H-l: xin gì, model áp gì, kiểu diễn đạt, và số ký tự
     * khối thinking trả về (Anthropic không tách token thinking khỏi output).
     */
    reasoning: z
        .object({
        requested: AiReasoning,
        applied: AiReasoning,
        style: ThinkingStyle,
        thinkingChars: z.number().int().nonnegative(),
    })
        .optional(),
});
/**
 * Prompt thô đúng như đã gửi cho model ở một lượt — APM-H-a.
 *
 * ⚠️ Dữ liệu nhạy cảm nhất hệ thống: chứa nguyên văn task, tên người, số giờ.
 * Chỉ ghi khi run có `debug: true`, chỉ người có `ai_config:manage` đọc được,
 * và **không bao giờ** đi qua SSE hay `GET /ai/runs/:id`.
 */
export const AiRunPrompt = z.object({
    kind: z.literal('prompt'),
    turn: z.number().int().positive(),
    system: z.string(),
    /** `Anthropic.MessageParam[]` — giữ nguyên hình dạng SDK, không diễn giải. */
    messages: z.array(z.unknown()),
    /** Tên tool model được thấy ở lượt này (sau khi lọc quyền). */
    tools: z.array(z.string()),
});
/**
 * Một lần kiểm kết quả cuối — APM-H-b. Hiện cho người xem: "AI nộp sai, đang sửa"
 * là thông tin họ cần để hiểu vì sao lượt chạy lâu hơn.
 *
 * `source`: kết quả tới từ tool `submit_result` (đường chính) hay từ khối JSON
 * trong văn bản (đường dự phòng — còn xảy ra nghĩa là prompt chưa dạy model gọi
 * tool). `partial`: `finalize` vẫn loại bớt ở lần cuối và hệ thống chấp nhận
 * phần đã lọc thay vì vứt cả kết quả.
 */
/**
 * Yêu cầu ban đầu của run — APM-H-d. Bước **ẩn**, ghi một lần ở đầu run.
 *
 * Có mặt để một fixture xuất từ `ai_run_step` chạy lại được: `runner.run()`
 * cần `prompt` và `params`, mà `ai_run` không có cột cho chúng. Là bước thay
 * cho cột vì nó cùng vòng đời và cùng quyền đọc với `prompt`/`response`.
 */
export const AiRunRequest = z.object({
    kind: z.literal('request'),
    /** V3 preserves authorized sandbox/replay overrides when a failed run resumes. */
    model: z.string().optional(),
    reasoning: AiReasoning.optional(),
    critique: z.boolean().optional(),
    promptVersion: z.string().optional(),
    sourceRunId: z.string().optional(),
    prompt: z.string(),
    params: z.record(z.string(), z.unknown()),
    allowClarify: z.boolean(),
});
/**
 * Phản hồi thô của model ở một lượt — APM-H-d. Bước **ẩn**, một bước mỗi lượt.
 *
 * `content` là các khối nội dung đúng như SDK trả về (text, tool_use, thinking),
 * `requestDigest` là băm của (system, messages, tools) đã gửi — không phải
 * nội dung, chỉ để fixture **cảnh báo** "prompt đã đổi so với lúc ghi".
 */
export const AiRunResponse = z.object({
    kind: z.literal('response'),
    turn: z.number().int().positive(),
    content: z.array(z.unknown()),
    stopReason: z.string().nullable(),
    requestDigest: z.string(),
});
/**
 * Một vòng agent phản biện — APM-CRIT-b (`docs/plan-critic-agent.md` §4.4).
 *
 * Khác `verify`: đây không phải lỗi của model. `verdict: 'revise'` = model chính
 * nhận `issues` và nộp lại; `ok` = chấp nhận. `stopped` nói vì sao **không**
 * hỏi tiếp: hết vòng, hết tiền phản biện, lặp cùng ý (`no_progress`), hay
 * người phản biện lỗi hạ tầng (`infra` — bỏ qua, không chặn run).
 */
export const AiRunCritique = z.object({
    kind: z.literal('critique'),
    round: z.number().int().positive(),
    rounds: z.number().int().nonnegative(),
    modelId: z.string().nullable(),
    /** Model phản biện khác họ với model vừa lập kết quả hay không. */
    family: z.enum(['other', 'same']).nullable(),
    verdict: z.enum(['ok', 'revise']).nullable(),
    issues: z.array(z.object({ path: z.string(), reason: z.string() })),
    costUsd: z.string(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    latencyMs: z.number().int().nonnegative(),
    stopped: z.enum(['rounds', 'budget', 'no_progress', 'infra']).optional(),
    error: z.string().optional(),
});
export const AiRunVerify = z.object({
    kind: z.literal('verify'),
    attempt: z.number().int().positive(),
    ok: z.boolean(),
    source: z.enum(['submit_result', 'text']),
    /** Lỗi Zod, tối đa 5, đường dẫn trường dạng `a.b.0.c`. */
    issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    /**
     * Phần bị `finalize()` loại kèm lý do. `source: 'critic'` = ý kiến của **agent
     * phản biện** (APM-FMT-h), không phải lỗi của model — chỉ số "verify hỏng"
     * phải bỏ những mục này ra (APM-CRIT-a; trước đó `format_document` hiện 50 % ảo).
     */
    rejected: z
        .array(z.object({
        path: z.string(),
        reason: z.string(),
        source: z.enum(['finalize', 'critic']).optional(),
    }))
        .optional(),
    partial: z.boolean().optional(),
    /**
     * Trường cấp gốc model nộp dưới dạng **chuỗi JSON** thay vì mảng/object, và
     * harness đã tự đọc lại trước Zod (đánh giá harness 2026-09-29). Có mặt = lượt
     * này đáng lẽ hỏng lần đầu; đếm nó khi đọc "Validate hỏng lần đầu".
     */
    coerced: z.array(z.string()).optional(),
    /**
     * Vì sao lượt này phải làm lại mà **không** trừ `verifyRetries` — APM-H-m:
     * `output_truncated` (bị `max_tokens` cắt giữa `submit_result`) hay
     * `empty_response` (model không nói gì, nhắc một lần).
     */
    reason: z.enum(['output_truncated', 'empty_response']).optional(),
    /**
     * Grounding — APM-H-n. `ungrounded`: Id/tên/biên nhận trong output không có
     * trong dữ liệu run này đọc được (đường dẫn trường). `grounded: true` khi qua —
     * đọc kèm `GROUNDED_NOTE`, không phải "AI đã kiểm".
     */
    ungrounded: z
        .array(z.object({
        path: z.string(),
        value: z.string(),
        reason: z.enum(['not_in_data', 'receipt_missing', 'not_in_receipt']),
    }))
        .optional(),
    grounded: z.boolean().optional(),
    /** Phần `finalize()` **không kiểm được** — không loại, không pass (APM-H-n). */
    unverified: z.array(z.object({ path: z.string(), reason: z.string() })).optional(),
});
/**
 * Câu chú thích **cố định** đi cùng `verify.grounded` — APM-H-n (mượn "execution
 * evidence only" của deer-flow). UI hiện nguyên câu, để dấu ✓ không bị đọc thành
 * "AI đã kiểm tính đúng".
 */
export const GROUNDED_NOTE = 'Id/tên có trong dữ liệu run này đọc được. Không xác nhận kết luận đúng.';
/**
 * Bước `context` — APM-H-j: sáu lớp của prompt với nguồn và số token, phần bị
 * bỏ, `systemHash` (cùng giá trị với bước `policy`), và `memoryIds` đã chèn
 * (để `ConfirmMemoryNotes` tăng đếm khi gợi ý được duyệt). Ghi ngay sau `policy`.
 */
export const AiRunContext = z.object({
    kind: z.literal('context'),
    blocks: z.array(ContextBlock),
    dropped: z.array(ContextDropped),
    systemHash: z.string(),
    memoryIds: z.array(z.string()),
    totals: z.object({
        system: z.number().int().nonnegative(),
        user: z.number().int().nonnegative(),
    }),
});
/**
 * Văn xuôi model viết trước khi gọi tool/nộp — APM-H-l đổi tên từ `thinking`
 * cho đúng nghĩa (đó không phải reasoning). `thinking` giữ làm alias một phiên
 * bản: bước cũ trong `ai_run_step` vẫn đọc được.
 */
export const AiRunProse = z.object({
    kind: z.literal('prose'),
    text: z.string(),
});
/**
 * Suy luận thật — APM-H-l. `mode: thinking` = khối thinking của model (nội dung
 * rút gọn, **chỉ khi debug**); `mode: scratchpad` = model không suy nghĩ được,
 * harness bảo viết kế hoạch trong `<scratchpad>` và tách ra khỏi `rationale`.
 */
export const AiRunReasoning = z.object({
    kind: z.literal('reasoning'),
    mode: z.enum(['thinking', 'scratchpad']),
    text: z.string(),
    chars: z.number().int().nonnegative(),
});
/** V3 controller progress. Draft is durable for resume, private to administrators. */
export const AiRunControl = z.object({
    kind: z.literal('control'),
    phase: z.enum(['evidence', 'synthesis', 'normalized', 'repair']),
    turn: z.number().int().nonnegative(),
    tools: z.array(z.string()).optional(),
    paths: z.array(z.string()).optional(),
    attempt: z.number().int().nonnegative().optional(),
    draftHash: z.string().optional(),
    draft: z.unknown().optional(),
    reviewIssues: z
        .array(z.object({ path: z.string().max(80), reason: z.string().max(200) }))
        .min(1)
        .max(4)
        .optional(),
});
export const AiRunStepBody = z.discriminatedUnion('kind', [
    AiRunContext,
    AiRunControl,
    AiRunThinking,
    AiRunProse,
    AiRunReasoning,
    AiRunToolCall,
    AiRunToolResult,
    AiRunQuestion,
    AiRunAnswer,
    AiRunResumed,
    AiRunTiming,
    AiRunDone,
    AiRunFailed,
    AiRunUsage,
    AiRunPrompt,
    AiRunVerify,
    AiRunCritique,
    AiRunPolicy,
    AiRunLimit,
    AiRunRequest,
    AiRunResponse,
]);
/**
 * Bước chỉ ghi DB, không phát cho người xem — xem `AiRunUsage`, `AiRunPrompt`.
 * `AiRunTracker.push(..., { hidden: true })` dùng danh sách này để từ chối một
 * bước `thinking` bị gắn nhầm cờ ẩn: ẩn là thuộc tính của **loại** bước.
 */
export const HIDDEN_RUN_STEP_KINDS = [
    'usage',
    'prompt',
    'request',
    'response',
];
/**
 * Trường ẩn theo **loại bước** — APM-H-e. Bước vẫn phát ra RAM/SSE nhưng thiếu
 * các trường này; recorder ghi đủ; `GET /ai/harness/runs/:id` chỉ trả cho
 * người có `ai_config:manage`. Cùng lý lẽ với `HIDDEN_RUN_STEP_KINDS`: ẩn là
 * thuộc tính của dữ liệu, không phải cờ của người gọi.
 */
export const HIDDEN_RUN_STEP_FIELDS = {
    tool_result: ['input', 'output', 'sentToModel', 'useId'],
    control: ['draft', 'reviewIssues'],
};
/** Bản không có trường ẩn — dùng cho RAM/SSE và cho người đọc không quản trị. */
export function stripHiddenStepFields(step) {
    const fields = HIDDEN_RUN_STEP_FIELDS[step.kind];
    if (!fields)
        return step;
    const copy = { ...step };
    for (const field of fields)
        delete copy[field];
    return copy;
}
export const AiRunStep = z.intersection(z.object({
    /** Tăng dần trong một lần chạy. Client dùng làm khoá React và để sắp thứ tự. */
    seq: z.number().int().nonnegative(),
    at: z.iso.datetime(),
    /**
     * Khối của harness mà bước này thuộc về — APM-H-m. Do **tracker** gán từ
     * `kind` (và từ sổ lỗi với bước `error`), không do skill: một skill gán sai
     * là mọi chỉ số theo stage sai theo. Tuỳ chọn để bước ghi trước H-m vẫn đọc được.
     */
    stage: AiRunStage.optional(),
}), AiRunStepBody);
/** Stage theo loại bước — hàm thuần, dùng chung tracker và trang Harness. */
export function stageOfStep(body) {
    switch (body.kind) {
        case 'control':
            return body.phase === 'repair' || body.phase === 'normalized' ? 'verify' : 'runtime';
        // `resume` ở nhóm này vì nối tiếp một run cũ là dựng lại **ngữ cảnh** của
        // lượt này — cùng giai đoạn với `request`/`context`.
        case 'request':
        case 'prompt':
        case 'context':
        case 'resume':
            return 'context';
        case 'response':
        case 'thinking':
        case 'prose':
        case 'reasoning':
        case 'usage':
        case 'question':
        case 'answer':
            return 'llm';
        case 'policy':
            return 'policy';
        case 'tool_call':
        case 'tool_result':
        case 'limit':
            return 'runtime';
        case 'verify':
        case 'critique':
            return 'verify';
        // `timing` cùng nhóm với `done`: bảng tổng kết của cả run, đứng ngay trước nó.
        case 'done':
        case 'timing':
            return 'result';
        case 'error':
            return stageOfError(body.code);
    }
}
/**
 * Sự kiện SSE `gap` — APM-H-m: dấu vết RAM của run đã bị dọn (API restart, hết
 * TTL) nhưng bản bền còn. Không phải bước (không `seq`, không lưu): client thấy
 * nó thì tải run từ `GET /ai/harness/runs/:id` thay vì chờ mãi.
 */
/**
 * Văn xuôi model **đang gõ** — sự kiện SSE `delta`, không phải bước (2026-09-29).
 *
 * `text` là **bản chụp** toàn bộ text của lượt gọi model hiện tại tính tới lúc
 * phát, không phải phần thêm: client thay nguyên khối, nên mất một sự kiện
 * giữa chừng cũng không sai chữ. Không có `seq`, không vào `ai_run`, và bước
 * thật kế tiếp (`prose`, `tool_call`…) thay thế nó.
 */
export const AiRunDeltaEvent = z.object({ text: z.string() });
/**
 * Sự kiện SSE `answer` của `/ai/runs/:runId/stream` — APM-CHAT2-m. Bản chụp **câu trả lời** mà
 * skill trả lời trong khung chat đang viết (trường `chatAnswer` đọc, lấy từ `submit_result` đang
 * stream). Khác `delta`: bước thật kế tiếp không thay nó — câu trả lời cuối, đã qua `finalize`,
 * tới qua phản hồi HTTP của lượt chat. Cùng hình với `delta`; client thay nguyên khối.
 */
export const AiRunAnswerEvent = AiRunDeltaEvent;
export const AiRunGapEvent = z.object({
    recovery: z.literal('load_run'),
    runId: z.uuid(),
});
export const AiRunStatus = z.enum(['running', 'clarifying', 'done', 'error']);
/** Đường chạy của run — APM-H-r. `deerflow` chưa có implementation; enum có mặt để contract không đổi khi có. */
export const AiRunner = z.enum(['internal', 'deerflow']);
export const AiRunTrace = z.object({
    runId: z.uuid(),
    skill: z.string(),
    status: AiRunStatus,
    startedAt: z.iso.datetime(),
    steps: z.array(AiRunStep),
});
/**
 * Trần số bước giữ cho một lần chạy.
 *
 * Một lần chạy bình thường là 3–15 bước (trần lượt của agent là 6). Trần ở đây
 * cao hơn hẳn để không cắt mất gì thật, nhưng vẫn có trần: một vòng lặp hỏng
 * không được phép ăn hết RAM của tiến trình duy nhất.
 */
export const MAX_RUN_STEPS = 200;
export const AiRunMode = z.enum(['real', 'sandbox', 'replay']);
/**
 * Một lượt chạy đã lưu bền — `GET /ai/harness/runs/:runId` (APM-H-a).
 *
 * Khác `AiRunTrace` (RAM, cho người dùng cuối): có đủ meta để so hai run, bước
 * không bị cắt, và có thể chứa `usage`/`prompt`. Bước `prompt` bị lược khi
 * người đọc không có `ai_config:manage`, và `promptRedacted` nói ra điều đó.
 */
/** Một dòng trong danh sách run gần đây của trang Harness — APM-H-h. Không có bước. */
export const AiRunSummary = z.object({
    runId: z.uuid(),
    skill: z.string(),
    mode: AiRunMode,
    runner: AiRunner,
    status: AiRunStatus,
    userId: z.uuid(),
    /** `null` = lượt không thuộc dự án nào — APM-TWR-d. */
    projectId: z.uuid().nullable(),
    promptVersion: z.string().nullable(),
    /**
     * Model đã sinh ra dấu vết này — APM-H-ae. `null` = run chưa có lượt model nào.
     *
     * Có ở cả `AiRunSummary` lẫn `AiRunRecord` vì nó trả lời hai câu: "chạy tiếp
     * được không" (record) và "hồi quy chi phí này có phải do đổi model không"
     * (summary — chỉ số tách được theo model mà không phải quét từng bước `usage`).
     */
    model: z.string().nullable().default(null),
    turns: z.number().int().nonnegative(),
    costUsd: z.string(),
    errorCode: z.string().nullable(),
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
});
export const AiRunListQuery = z.object({
    skill: z.string().min(1).max(60).optional(),
    projectId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const AiRunRecord = z.object({
    runId: z.uuid(),
    orgId: z.uuid(),
    userId: z.uuid(),
    /** `null` = lượt không thuộc dự án nào — APM-TWR-d. */
    projectId: z.uuid().nullable(),
    skill: z.string(),
    mode: AiRunMode,
    runner: AiRunner,
    debug: z.boolean(),
    promptVersion: z.string().nullable(),
    /** Model đã sinh ra dấu vết này — APM-H-ae. `resume` so với model hiện tại trước khi phát lại. */
    model: z.string().nullable().default(null),
    status: AiRunStatus,
    turns: z.number().int().nonnegative(),
    costUsd: z.string(),
    errorCode: z.string().nullable(),
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    steps: z.array(AiRunStep),
    promptRedacted: z.boolean(),
});
/**
 * Fixture của một run — APM-H-d: đủ để chạy lại skill qua runner thật với
 * gateway giả, **không cần** key và không cần MySQL.
 *
 * Kịch bản khớp **theo lượt**, không theo nội dung prompt: khớp nội dung là
 * test vỡ mỗi lần sửa một chữ trong prompt. `requestDigest` chỉ để cảnh báo.
 */
export const AiRunFixtureTurn = z.object({
    turn: z.number().int().positive(),
    requestDigest: z.string(),
    content: z.array(z.unknown()),
    stopReason: z.string().nullable(),
    usage: z
        .object({
        inputTokens: z.number().int().nonnegative(),
        outputTokens: z.number().int().nonnegative(),
        cacheReadInputTokens: z.number().int().nonnegative(),
        cacheCreationInputTokens: z.number().int().nonnegative(),
    })
        .optional(),
});
export const AiRunFixture = z.object({
    harness: AiHarnessSelection.optional(),
    version: z.literal(1),
    runId: z.uuid(),
    skill: z.string(),
    promptVersion: z.string().nullable(),
    request: z.object({
        prompt: z.string(),
        params: z.record(z.string(), z.unknown()),
        allowClarify: z.boolean(),
    }),
    turns: z.array(AiRunFixtureTurn),
    exportedAt: z.iso.datetime(),
});
/**
 * Lớp lỗi của một lượt chạy — APM-H-ac. Danh mục **đóng**; mô tả từng lớp và
 * cách định vị bước sai đầu tiên ở [`docs/ai-failure-taxonomy.md`](../../../docs/ai-failure-taxonomy.md).
 *
 * Đóng vì một danh mục mở thì sau ba tháng mỗi lượt đánh giá đẻ ra một tên lớp
 * mới, và không ai đếm được gì theo thời gian — đúng thứ mà việc quy kết tồn
 * tại để làm. Thêm một lớp là sửa **cả hai** chỗ, và có test kiến trúc canh.
 */
export const AiFailureClass = z.enum([
    'misread_request',
    'skipped_process',
    'tool_error',
    'fabricated',
    'number_mismatch',
    'wrong_report',
    'stopped_early',
    'capped',
    'abnormal_end',
]);
/**
 * Phạm vi của bài học rút từ một lỗi — APM-H-ar (mượn INV / DOM của Harness2).
 *
 * `invariant`: lỗi nhìn thấy được mà không cần biết skill định làm gì (nộp khi
 * chưa đọc gì, chạm trần, tool hỏng, gateway chết) — bài học đúng cho **mọi**
 * skill, chỗ sửa là lớp 1 (luật harness, `defaults.ts`, engine bản kế).
 * `domain`: phải biết skill phải trả ra gì mới kết luận được (hiểu sai yêu cầu,
 * số sai, kết luận không khớp dữ liệu) — bài học của **một** skill ở **một** tổ
 * chức, chỗ sửa là prompt của skill ấy hoặc `org_knowledge` theo tag.
 *
 * Cùng hai giá trị với `PromptExperienceScope` ở `ai-prompt.ts`, cố ý tách
 * enum: một bên là phạm vi của **lỗi** đã quy kết, một bên là phạm vi của
 * **bài học** người viết lúc kích hoạt prompt — `recurring` ghép hai nguồn ấy.
 */
export const AiFailureScope = z.enum(['invariant', 'domain']);
/**
 * Phạm vi **mặc định** theo lớp lỗi — danh mục đóng, một hàng cho mỗi lớp
 * (`Record` nên thiếu một lớp là lỗi biên dịch; test kiến trúc canh bảng §2 của
 * `ai-failure-taxonomy.md` nói cùng một điều). `attributeRun()` điền theo đây;
 * người sửa được ở `PUT …/attribution`.
 *
 * Luật chung: bốn lớp **luật tất định** tìm ra mà không cần hiểu miền là
 * `invariant`; bốn lớp phải **đọc dấu vết** mới kết luận được là `domain`.
 * Ngoại lệ duy nhất là `fabricated`: luật tìm ra, nhưng id/số nào là "có thật"
 * phụ thuộc dữ liệu của skill, và chỗ sửa gần như luôn là prompt hoặc
 * `finalize()` của skill ấy — nên là `domain`.
 */
export const FAILURE_CLASS_DEFAULT_SCOPE = {
    misread_request: 'domain',
    skipped_process: 'invariant',
    tool_error: 'invariant',
    fabricated: 'domain',
    number_mismatch: 'domain',
    wrong_report: 'domain',
    stopped_early: 'domain',
    capped: 'invariant',
    abnormal_end: 'invariant',
};
/**
 * Bản ghi quy kết một lượt chạy hỏng — APM-H-ac.
 *
 * Một điểm số không học được gì; bản ghi này học được vì nó có **bốn** thứ: lớp
 * lỗi, vị trí bước sai đầu tiên, bằng chứng đọc lại được, và mức tin cậy. Thiếu
 * `firstBadStep` thì mọi câu "lỗi ở chỗ nào" là một đoạn văn; thiếu `confidence`
 * thì một phán đoán được đếm ngang một phép kiểm.
 */
export const AiRunAttribution = z.object({
    runId: z.uuid(),
    cls: AiFailureClass,
    /** `seq` của bước sai **đầu tiên**. `null` khi tín hiệu không gắn với bước nào. */
    firstBadStep: z.number().int().nonnegative().nullable(),
    /** Trường, giá trị, tên tool — đọc lại được mà không cần mở run. */
    evidence: z.record(z.string(), z.unknown()),
    /** `rule`: luật tất định tìm ra. `guess`: người hoặc model kết luận. */
    confidence: z.enum(['rule', 'guess']),
    /** Lớp còn lại — có mặt để không ai đếm ba lần thử lại thành ba lỗi. */
    secondary: z.array(AiFailureClass),
    /** Bài học rút ra đúng cho mọi skill (`invariant`) hay chỉ skill này (`domain`) — APM-H-ar. */
    scope: AiFailureScope,
    /** Một câu của người. Không thay được `evidence`. */
    note: z.string().max(500).nullable(),
    recordedAt: z.iso.datetime(),
    recordedBy: z.uuid().nullable(),
});
/** Ghi quy kết cho một run — `PUT /ai/harness/runs/:runId/attribution`. */
export const AttributeRunBody = z.object({
    cls: AiFailureClass,
    firstBadStep: z.number().int().nonnegative().nullable().default(null),
    evidence: z.record(z.string(), z.unknown()).default({}),
    confidence: z.enum(['rule', 'guess']).default('guess'),
    secondary: z.array(AiFailureClass).max(8).default([]),
    /** Bỏ trống ⇒ server điền theo `FAILURE_CLASS_DEFAULT_SCOPE[cls]`; gửi là ghi đè (APM-H-ar). */
    scope: AiFailureScope.optional(),
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * Cái đã ghi và cái luật **đề nghị** — `GET /ai/harness/runs/:runId/attribution`.
 *
 * Trả cả hai vì chúng trả lời hai câu khác nhau: `recorded` là kết luận đã chốt
 * (có thể do người, có thể sai), `suggested` là thứ đọc thẳng từ dấu vết ngay
 * lúc này. Lệch nhau **là** thông tin: hoặc người biết điều luật không biết,
 * hoặc bản ghi đã cũ so với dấu vết.
 */
export const RunAttributionView = z.object({
    recorded: AiRunAttribution.nullable(),
    suggested: z
        .object({
        cls: AiFailureClass,
        firstBadStep: z.number().int().nonnegative().nullable(),
        evidence: z.record(z.string(), z.unknown()),
        secondary: z.array(AiFailureClass),
        scope: AiFailureScope,
    })
        .nullable(),
});
//# sourceMappingURL=ai-run.js.map